import { Bar, BarConfig } from '@ant-design/charts';
import { useLazyQuery } from '@apollo/client';
import {
  ChallengeApplicationCount,
  ChallengeManagerStatistics,
  GET_CHALLENGE_MANAGER_STATS,
} from '@frontend/data-access';
import { Card, Select } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

const states = ['open', 'closed', 'upcoming', 'draft', 'archived'];

function ChallengeManagerStats({ className }: { className?: string }) {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<ChallengeApplicationCount[]>([]);
  const [selectedManagers, setSelectedManagers] = useState<string[]>([]);

  const COLOR_PLATE_10 = [
    '#26D426',
    '#2888ED',
    '#62BAFC',
    '#36CBCB',
    '#525355',
    '#A3B1BF',
    '#8B8B8B',
    '#1890FF',
  ];

  const LABEL_MAP = useMemo(() => {
    return {
      upcoming: [t('upcoming')],
      draft: [t('draft')],
      archived: [t('archived')],
      open: [t('registration_open')],
      closed: [t('registration_closed')],
    };
  }, [t]);

  const [getData, { loading }] = useLazyQuery<{
    getChallengeManagerStatistics: ChallengeManagerStatistics[];
  }>(GET_CHALLENGE_MANAGER_STATS, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const result = [];
      for (const item of data.getChallengeManagerStatistics ?? []) {
        for (const value of states) {
          result.push({
            name: item.user,
            count: item[value],
            state: LABEL_MAP[value],
          });
        }
      }
      setData(result);
    },
  });

  useEffect(() => {
    getData({
      variables: {
        order: true,
      },
    });
  }, [getData]);

  // Filter data based on the selected managers
  const filteredData = data.filter((item) =>
    selectedManagers.length > 0 ? selectedManagers.includes(item.name) : true
  );

  const config: BarConfig = {
    data: filteredData, // Use filtered data
    stack: true,
    loading: loading,
    xField: 'count',
    yField: 'name',
    seriesField: 'state',
    color: ({ state }) => COLOR_PLATE_10[state],
    legend: { position: 'bottom', flipPage: false },
    xAxis: {
      label: {
        style: {
          fontFamily: 'Lufga',
          fill: 'rgba(0, 0, 0, 0.65)',
        },
      },
    },
    yAxis: {
      label: {
        style: {
          fontFamily: 'Lufga',
          fill: 'rgba(0, 0, 0, 0.65)',
          textAlign: 'right',
        },
        labelFormatter: (value) => {
          const str = value.toString();
          if (str.length > 30) {
            return str.slice(0, 30) + '...';
          }
          return str;
        },
      },
      y: {
        label: {
          style: {
            fontFamily: 'Lufga',
            fill: 'rgba(0, 0, 0, 0.65)',
          },
        },
      },
    },
    label: {
      text: ({ count }) => (count > 0 ? count : ''),
      position: 'inside',
      style: {
        fontFamily: 'Lufga',
      },
    },
  };

  return (
    <Card
      style={{
        borderRadius: 20,
      }}
      className={`${className}`}
    >
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-sm text-gray-900 font-medium">
              {t('challenge_managers')}
            </div>
            <span className="text-gray-700 font-normal">
              {t('number_of_created_challenges')}
            </span>
          </div>
          <div>
            <Select
              mode="multiple"
              placeholder={t('select_challenge_managers')}
              className="!w-52"
              value={selectedManagers}
              onChange={(value) => setSelectedManagers(value)}
              options={Array.from(new Set(data.map((item) => item.name))).map(
                (name) => ({ label: name, value: name })
              )}
              allowClear
              maxTagCount="responsive"
              style={{ borderRadius: 24 }}
              optionFilterProp="label"
            />
          </div>
        </div>
        <Bar loading={loading} {...config} />
      </div>
    </Card>
  );
}

export default ChallengeManagerStats;
